import { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Toolbar,
  Divider,
  Button,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { createChat, updateChat } from '@/actions/chat';
import { useUser } from '@/providers/UserContext';

import LoadingMask from '@/components/shared/LoadingMask';

import SearchDocuments from './SearchDocuments';
import { useApi } from '@/providers/ApiContext';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useLocation } from 'react-router';
import { getChat } from '@/actions/chat';
import AuditNameField from './AuditNameField';
import { LoadingButton } from '@mui/lab';
import CHAT_TYPES from '@/components/chatTypes';
import type { Chat } from '@/types/apiTypes';

function ExternalAuditForm(): React.JSX.Element {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const [chat, setChat] = useState<Chat | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pristine, setPristine] = useState<boolean>(true);
  const [doc_id, setDocId] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<boolean | string>(false);

  const sp = new URLSearchParams(location.search);
  const audit_id = sp.get('audit_id');

  useEffect(() => {
    if (user?.entity_id && !chat && api) {
      if (audit_id) {
        getChat(api, audit_id)
          .then((data) => {
            setChat(data);
            setName(data?.name || '');
            setDocId(data?.doc_id || null);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }, [user?.entity_id, audit_id, chat, api]);

  const handleSubmit = async (): Promise<void> => {
    if (!doc_id || !api) return;
    setLoading(true);
    const payload = {
      name,
      doc_id,
      type: CHAT_TYPES.QUARTERMASTER,
    };
    let updated_chat: Chat;
    if (audit_id && chat) {
      updated_chat = await updateChat(api, chat.entity_id, payload);
    } else {
      updated_chat = await createChat(api, payload);
    }
    navigate(`/quartermaster/externalAudit/${updated_chat.entity_id}`);
  };

  if (!user?.entity_id || (!chat && audit_id)) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton sx={{ mr: 2 }} onClick={() => navigate(-1)}>
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {chat ? 'Edit Audit' : 'New Audit'}
        </Typography>
      </Toolbar>
      <Divider />

      <Grid container spacing={2} style={{ padding: '15px' }}>
        <Grid size={12}>
          <AuditNameField
            value={name}
            setValue={setName}
            error={name_error}
            setError={setNameError}
            setChanged={() => setPristine(false)}
          />
        </Grid>
        {doc_id ? (
          <>
            <Grid size={12}>
              <Alert severity='success'>
                <AlertTitle>
                  Interrogating document <b>{doc_id}</b>
                </AlertTitle>
              </Alert>
            </Grid>
            <Grid size={12}>
              <Button
                onClick={() => {
                  setDocId(null);
                }}
                variant='outlined'
              >
                Reset Selection
              </Button>
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              <Toolbar>
                <Stack spacing={2} direction='row'>
                  <LoadingButton
                    variant='contained'
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={
                      pristine ||
                      Boolean(name_error) ||
                      !Boolean(name) ||
                      !Boolean(doc_id)
                    }
                  >
                    Submit
                  </LoadingButton>

                  <Button
                    variant='outlined'
                    color='inherit'
                    onClick={() => navigate(-1)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Toolbar>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={12}>
              <SearchDocuments
                selectDocument={(doc_id: string, _title: string) => {
                  setDocId(doc_id);
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

export default ExternalAuditForm;
